// ============================================
// @dx form
// StaticSelects.tsx
// ============================================
import React from "react";
import { Select, SelectProps } from "antd";
import { Tag } from "@ds/display/Tag";
import { Dot } from "@ds/display/Dot";
import { Inline } from "@ds/layout/Inline";
import { useTranslation } from "react-i18next";

import { DOCUMENT_TYPE_VALUES, DOCUMENT_STATUS_VALUES, REVIEW_STATUS_VALUES, COMMENT_TAG_VALUES, CATEGORY_VALUES, EMPLOYEE_ROLE_VALUES, CONTACT_PURPOSE_VALUES, VAT_RATE_CODE_VALUES } from "@shared/types/enumValues";
import { DocumentType, DocumentStatus, ReviewStatus, CommentTag, CompanyCategory, EmployeeRole, ContactPurpose, VatRateCode } from "@shared/types";
import { COMMENT_TAG_COLORS, DOCUMENT_TYPE_COLORS, DOCUMENT_STATUS_COLORS, REVIEW_STATUS_COLORS } from "../tokens/domainTones";
import { Box } from "@ds/layout/Box";
import { formatVatRateCode } from "@shared/calc";
import { useCountries } from "@ds/connected/useCountries";

/** Boolean select for request_dpr (Tak/Nie). */
export const DprRequestSelect: React.FC<Omit<SelectProps, "children">> = (props) => {
  const { t } = useTranslation();
  return (
    <Select {...props}>
      <Select.Option value={true}>{t("common.yes", "Tak")}</Select.Option>
      <Select.Option value={false}>{t("common.no", "Nie")}</Select.Option>
    </Select>
  );
};

// ── Custom selects (kept manually — custom render, defaults, or formatter) ────

export const DocumentTypeSelect: React.FC<
  Omit<SelectProps<DocumentType>, "children">
> = (props) => {
  const { t } = useTranslation();
  return (
    <Select<DocumentType> {...props}>
      {DOCUMENT_TYPE_VALUES.map((type) => (
        <Select.Option key={type} value={type}>
          <Tag
            label={t(`documents.document_type.${type}`)}
            tone={DOCUMENT_TYPE_COLORS[type] ?? "grey"}
            size="sm"
          />
        </Select.Option>
      ))}
    </Select>
  );
};

export const DocumentStatusSelect: React.FC<
  Omit<SelectProps<DocumentStatus>, "children">
> = (props) => {
  const { t } = useTranslation();
  return (
    <Select<DocumentStatus> {...props}>
      {DOCUMENT_STATUS_VALUES.map((status) => (
        <Select.Option key={status} value={status}>
          <Tag
            label={t(`documents.document_status.${status}`)}
            tone={DOCUMENT_STATUS_COLORS[status] ?? "grey"}
            size="sm"
          />
        </Select.Option>
      ))}
    </Select>
  );
};

export const ReviewStatusSelect: React.FC<
  Omit<SelectProps<ReviewStatus>, "children">
> = (props) => {
  const { t } = useTranslation();
  return (
    <Select<ReviewStatus> {...props}>
      {REVIEW_STATUS_VALUES.map((status) => (
        <Select.Option key={status} value={status}>
          <Tag
            label={t(`documents.review_status.${status}`)}
            tone={REVIEW_STATUS_COLORS[status] ?? "grey"}
            size="sm"
          />
        </Select.Option>
      ))}
    </Select>
  );
};

// Comment-tag picker rendered as a chip-button (matches design).
// Selected state: tag's soft background + dark text + colored border.
// Empty state: white surface + gray border + muted "Bez etykiety" placeholder.
// AntD Select is the engine — keeps keyboard nav, a11y, etc. — `variant="borderless"`
// removes AntD's own bg/border so the chip-styled wrapper shows through.
import type { Tone } from "@ds/tokens/tones";
function chipTone(tag: CommentTag | null): Tone {
  if (!tag) return "muted";
  return COMMENT_TAG_COLORS[tag] ?? "grey";
}

export const CommentTagSelect: React.FC<
  Omit<SelectProps<CommentTag>, "children">
> = (props) => {
  const { t } = useTranslation();
  const value = props.value ?? null;
  const tone = chipTone(value);

  return (
    <Box
      as="span"
      tone={tone}
      border
      shape="rounded-sm"
    >
      <Select<CommentTag>
        allowClear
        variant="borderless"
        placeholder="Bez etykiety"
        optionLabelProp="label"
        popupMatchSelectWidth={false}
        styles={{
          popup: { root: { minWidth: 200 } },
        }}
        optionRender={(option) => {
          const tag = option.value as CommentTag;
          return (
            <Inline as="span" inline gap="xs">
              <Dot tone={COMMENT_TAG_COLORS[tag]} size="sm" />
              {t(`comments.tag.${tag}`)}
            </Inline>
          );
        }}
        {...props}
        style={{
          minWidth: 130,
          fontSize: 12,
          fontWeight: 500,
          color: "inherit",
          ...props.style,
        }}
      >
        {COMMENT_TAG_VALUES.map((tag) => (
          <Select.Option key={tag} value={tag} label={t(`comments.tag.${tag}`)}>
            {t(`comments.tag.${tag}`)}
          </Select.Option>
        ))}
      </Select>
    </Box>
  );
};

export const CategorySelect: React.FC<
  Omit<SelectProps<CompanyCategory>, "children">
> = (props) => {
  const { t } = useTranslation();
  return (
    <Select<CompanyCategory>
      mode="multiple"
      popupMatchSelectWidth={false}
      styles={{ popup: { root: { minWidth: 220 } } }}
      {...props}
    >
      {CATEGORY_VALUES.map((category) => (
        <Select.Option key={category} value={category}>
          {t(`companies.categories.${category}`)}
        </Select.Option>
      ))}
    </Select>
  );
};

export const EmployeeRoleSelect: React.FC<
  Omit<SelectProps<EmployeeRole>, "children">
> = (props) => {
  const { t } = useTranslation();
  return (
    <Select<EmployeeRole> mode="multiple" {...props}>
      {EMPLOYEE_ROLE_VALUES.map((role) => (
        <Select.Option key={role} value={role}>
          {t(`employees.roles.${role}`)}
        </Select.Option>
      ))}
    </Select>
  );
};

export const ContactPurposeSelect: React.FC<
  Omit<SelectProps<ContactPurpose>, "children">
> = (props) => {
  const { t } = useTranslation();
  return (
    <Select<ContactPurpose> mode="multiple" {...props}>
      {CONTACT_PURPOSE_VALUES.map((purpose) => (
        <Select.Option key={purpose} value={purpose}>
          {t(`contacts.purposes.${purpose}`)}
        </Select.Option>
      ))}
    </Select>
  );
};

export const VatRateSelect: React.FC<
  Omit<SelectProps<VatRateCode>, "children">
> = (props) => (
  <Select<VatRateCode> {...props}>
    {VAT_RATE_CODE_VALUES.map((code) => (
      <Select.Option key={code} value={code}>
        {formatVatRateCode(code)}
      </Select.Option>
    ))}
  </Select>
);

export const CountrySelect: React.FC<Omit<SelectProps, "children">> = (props) => {
  const { countryOptions } = useCountries();
  return (
    <Select
      showSearch
      optionFilterProp="label"
      options={countryOptions}
      popupMatchSelectWidth={false}
      styles={{ popup: { root: { minWidth: 220 } } }}
      {...props}
    />
  );
};
